export class DocumentationRenderer {
  static renderApiDocumentation(): string {
    return `
      <div class="documentation-container">
        <h1>Survey Web App - API Documentation</h1>
        
        <div>
          <section>
            <h2>Overview</h2>
            <p>This survey web app is designed to be embedded in mobile applications via WebView. It supports multiple question types, real-time validation, and seamless integration with your mobile app through URL parameters.</p>
          </section>

          <section>
            <h2>Supported Question Types</h2>
            <ul>
              <li><strong>text</strong>: Simple text input field for short answers.</li>
              <li><strong>textarea</strong>: Multi-line text input for longer responses.</li>
              <li><strong>email</strong>: Email input with built-in validation.</li>
              <li><strong>number</strong>: Numeric input field that accepts integers and decimals with optional min/max validation.</li>
              <li><strong>rating</strong>: Star or numeric rating input.</li>
              <li><strong>select</strong>: Dropdown selection from predefined options.</li>
              <li><strong>dropdown</strong>: Single choice from dropdown menu (renders as dropdown).</li>
              <li><strong>radio</strong>: Single choice from multiple options (renders as radio buttons).</li>
              <li><strong>checkbox</strong>: Multiple choice selection (renders as checkboxes).</li>
              <li><strong>date</strong>: Date picker for selecting dates.</li>
              <li><strong>time</strong>: Time picker for selecting a time.</li>
              <li><strong>grid</strong>: Grid question for rating multiple statements against the same set of options.</li>
            </ul>
            <p><em>Note: 'select' and 'radio' both render as radio buttons, 'dropdown' renders as dropdown menu.</em></p>
            <pre><code>{
  "id": "q1",
  "question": "What is your name?",
  "type": "text",
  "placeholder": "Enter your full name",
  "required": true
}</code></pre>
            </div>

            <div>
              <h3>2. Email Input</h3>
              <p>Email input with built-in validation.</p>
              <pre><code>{
  "id": "q2",
  "question": "Your email address",
  "type": "email",
  "placeholder": "example@email.com",
  "required": true
}</code></pre>
            </div>

            <div>
              <h3>3. Number Input</h3>
              <p>Numeric input that accepts both integers and decimals. Supports optional min/max validation with custom error messages.</p>
              <pre><code>{
  "id": "q3",
  "question": "What is your age?",
  "type": "number",
  "placeholder": "Enter your age",
  "required": true,
  "validation": {
    "type": "number",
    "rules": {
      "min": 18,
      "max": 100
    },
    "errorMessage": "Age must be between 18 and 100"
  }
}</code></pre>
              <p><em>Note: The validation object is optional. You can omit rules for unrestricted numeric input, or specify only min or only max.</em></p>
            </div>

            <div>
              <h3>4. Time Input</h3>
              <p>Time picker for selecting time.</p>
              <pre><code>{
  "id": "q4",
  "question": "What time do you prefer?",
  "type": "time",
  "required": true
}</code></pre>
            </div>

            <div>
              <h3>5. Date Input</h3>
              <p>Date picker for selecting dates.</p>
              <pre><code>{
  "id": "q5",
  "question": "Your birth date",
  "type": "date",
  "required": true
}</code></pre>
            </div>

            <div>
              <h3>6. Textarea</h3>
              <p>Multi-line text input for longer responses.</p>
              <pre><code>{
  "id": "q6",
  "question": "Please describe your experience",
  "type": "textarea",
  "placeholder": "Enter detailed response...",
  "required": false
}</code></pre>
            </div>

            <div>
              <h3>7. Dropdown</h3>
              <p>Dropdown selection from predefined options.</p>
              <pre><code>{
  "id": "q7",
  "question": "Select your country",
  "type": "dropdown",
  "options": ["USA", "Canada", "UK", "Australia"],
  "required": true
}</code></pre>
            </div>

            <div>
              <h3>8. Radio Buttons</h3>
              <p>Single choice from multiple options (renders as radio buttons).</p>
              <pre><code>{
  "id": "q8",
  "question": "What is your preferred contact method?",
  "type": "radio",
  "options": ["Email", "Phone", "SMS", "Mail"],
  "required": true
}</code></pre>
            </div>

            <div>
              <h3>9. Select</h3>
              <p>Single choice from multiple options (renders as radio buttons).</p>
              <pre><code>{
  "id": "q9",
  "question": "How satisfied are you with our service?",
  "type": "select",
  "options": ["Very Satisfied", "Satisfied", "Neutral", "Dissatisfied", "Very Dissatisfied"],
  "required": true
}</code></pre>
            </div>

            <div>
              <h3>10. Checkboxes</h3>
              <p>Multiple choice selection.</p>
              <pre><code>{
  "id": "q10",
  "question": "Which programming languages do you know?",
  "type": "checkbox",
  "options": ["JavaScript", "Python", "Java", "C++", "Go"],
  "required": false
}</code></pre>
            </div>

            <div>
              <h3>11. Star Rating</h3>
              <p>Rating scale with customizable range.</p>
              <pre><code>{
    "id": "q11",
    "question": "Rate your experience",
    "type": "rating",
    "min": 1,
    "max": 5,
    "required": true
}</code></pre>
            </div>

            <div>
              <h3>12. Grid Question</h3>
              <p>Grid question for rating multiple statements against the same set of options. Perfect for Likert scale surveys.</p>
              <pre><code>{
    "id": "q12",
    "question": "Please rate your agreement with the following statements:",
    "type": "grid",
    "rows": [
        "The product is easy to use",
        "Customer support is helpful",
        "The pricing is fair"
    ],
    "columns": [
        "Strongly agree",
        "Agree",
        "Neutral",
        "Disagree",
        "Strongly disagree"
    ],
    "required": true
}</code></pre>
            </div>
          </section>

          <section>
            <h2>Survey JSON Format</h2>
            <p>Complete survey structure with multiple questions:</p>
          <pre><code>{
  "id": "survey-001",
  "title": "Customer Feedback Survey",
  "payout_type": "CASH",
  "payout_type_message": "You will receive $10 immediately.",
  "payout_type_message_title": "Cash Payout", 
  "questions": [
    {
      "id": "q1",
      "question": "What is your name?",
      "type": "text",
      "placeholder": "Enter your full name",
      "required": true
    },
    {
      "id": "q2",
      "question": "How would you rate our service?",
      "type": "rating",
      "min": 1,
      "max": 5,
      "required": true
    },
    {
      "id": "q3",
      "question": "Would you recommend us?",
      "type": "radio",
      "options": ["Yes", "No"],
      "required": true
    }
  ]
}</code></pre>

            <h3>Accessing Payout Information</h3>
            <p>You can access the payout information programmatically using the survey controller:</p>
            <pre><code>const fullSurveyData = this.surveyController?.getFullSurveyData();
const payoutType = fullSurveyData?.questions?.payout_type;
const payoutTypeMessage = fullSurveyData?.questions?.payout_type_message;
const payoutTypeMessageTitle = fullSurveyData?.questions?.payout_type_message_title;</code></pre>
          </section>

          <section>
            <h2>URL Parameters</h2>
            <p>The survey app accepts the following URL parameters for integration:</p>
            
            <table>
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Description</th>
                  <th>Required</th>
                  <th>Example</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>userId</code></td>
                  <td>Unique identifier for the user (optional for anonymous surveys)</td>
                  <td>No</td>
                  <td>123</td>
                </tr>
                <tr>
                  <td><code>surveyId</code></td>
                  <td>Specific survey identifier</td>
                  <td>No</td>
                  <td>survey-001</td>
                </tr>
                <tr>
                  <td><code>anonymous</code></td>
                  <td>Enable anonymous mode - uses device ID when no userId provided (defaults to false)</td>
                  <td>No</td>
                  <td>true</td>
                </tr>
                <tr>
                  <td><code>referralCode</code></td>
                  <td>Referral code for rewards (defaults to 'default')</td>
                  <td>No</td>
                  <td>ABC</td>
                </tr>
                <tr>
                  <td><code>emailAddress</code></td>
                  <td>User email address for tracking/contact</td>
                  <td>No</td>
                  <td>user@example.com</td>
                </tr>
                <tr>
                  <td><code>source</code></td>
                  <td>Traffic source or campaign identifier</td>
                  <td>No</td>
                  <td>mobile_app</td>
                </tr>
              </tbody>
            </table>

            <h3>Example URL Formats</h3>
            <p><strong>Standard Survey:</strong></p>
            <pre><code>https://survey.askbilly.ai/?userId=123&referralCode=ABC&surveyId=survey-001&emailAddress=user@example.com&source=mobile_app</code></pre>
            <p><strong>Anonymous Survey:</strong></p>
            <pre><code>https://survey.askbilly.ai/?anonymous=true&surveyId=survey-001&emailAddress=user@example.com&source=mobile_app</code></pre>
          </section>

          <section>
            <h2>Integration Examples</h2>
            
            <h3>Kotlin/Android Integration</h3>
            <pre><code>// Standard Survey
val userId = "123"
val referralCode = "ABC"
val surveyId = "survey-001"
val emailAddress = "user@example.com"
val source = "mobile_app"

val url = "https://survey.askbilly.ai/?userId=\${userId}&referralCode=\${referralCode}&surveyId=\${surveyId}&emailAddress=\${emailAddress}&source=\${source}"

// Anonymous Survey
val anonymousUrl = "https://survey.askbilly.ai/?anonymous=true&surveyId=\${surveyId}&emailAddress=\${emailAddress}&source=\${source}"

val webView = findViewById&lt;WebView&gt;(R.id.webview)
webView.settings.javaScriptEnabled = true
webView.loadUrl(url) // or anonymousUrl for anonymous surveys</code></pre>

            <h3>iOS/Swift Integration</h3>
            <pre><code>// Standard Survey
let userId = "123"
let referralCode = "ABC"
let surveyId = "survey-001"
let emailAddress = "user@example.com"
let source = "mobile_app"

let urlString = "https://survey.askbilly.ai/?userId=\(userId)&referralCode=\(referralCode)&surveyId=\(surveyId)&emailAddress=\(emailAddress)&source=\(source)"

// Anonymous Survey
let anonymousUrlString = "https://survey.askbilly.ai/?anonymous=true&surveyId=\(surveyId)&emailAddress=\(emailAddress)&source=\(source)"

let url = URL(string: urlString)! // or anonymousUrlString for anonymous surveys
let webView = WKWebView()
let request = URLRequest(url: url)
webView.load(request)</code></pre>
          </section>

          <section>
            <h2>Response Format</h2>
            <p>When the survey is submitted, the response is sent in this format:</p>
            <pre><code>{
  "userId": "123",
  "referralCode": "ABC", 
  "surveyId": "survey-001",
  "emailAddress": "user@example.com",
  "source": "mobile_app",
  "timestamp": "2025-07-11T10:30:00Z",
  "answers": {
    "q1": "John Doe",
    "q2": "5",
    "q3": "yes"
  }
}</code></pre>
          </section>

          <section>
            <h2>Validation Rules</h2>
            <ul>
              <li><strong>Required fields:</strong> Must be completed before proceeding</li>
              <li><strong>Email:</strong> Must be valid email format</li>
              <li><strong>Number:</strong> Must be a valid numeric value (integer or decimal). If validation rules are specified, must be within min/max range. Custom error messages supported.</li>
              <li><strong>Date:</strong> Must be valid date format</li>
              <li><strong>Rating:</strong> Must be within specified range</li>
              <li><strong>Grid:</strong> All rows must be answered if the question is required</li>
            </ul>

            <h3>Number Validation Examples</h3>
            <pre><code>// Basic number input (no restrictions)
{
  "type": "number",
  "placeholder": "Enter any number"
}

// Integer or decimal with min/max
{
  "type": "number",
  "validation": {
    "type": "number",
    "rules": {
      "min": 0,
      "max": 100
    },
    "errorMessage": "Value must be between 0 and 100"
  }
}

// Only minimum value
{
  "type": "number",
  "validation": {
    "type": "number",
    "rules": {
      "min": 0
    },
    "errorMessage": "Value must be positive"
  }
}</code></pre>
          </section>
        </div>
      </div>
    `;
  }
}
